<script lang="ts">
  /**
   * A Portfolio contact's Briefing tab: a status update and a cited fact list
   * (backend services/fund_portfolio_briefing.py). Far smaller than the LP
   * `BriefingView`: no sections, edit lock, research or doc view, so it loads and
   * polls on its own instead of sharing the LP briefing store.
   */
  import { untrack } from 'svelte';
  import {
    generateFundPortfolioBriefing, getFundPortfolioBriefing, type PortfolioBriefing,
  } from '../api/fund-ir';

  type Source = PortfolioBriefing['sources'][number];

  let {
    counterpartyId, companyName, agent, readOnly = false, onOpenSource,
  }: {
    counterpartyId: string;
    companyName: string;
    agent: string;
    readOnly?: boolean;
    onOpenSource?: (source: Source) => void;
  } = $props();

  const POLL_MS = 2500;
  let briefing = $state<PortfolioBriefing | null>(null);
  let loading = $state(false);
  let generating = $state(false);
  let error = $state('');
  // A reply for a contact we have since left is dropped, not shown on the next one.
  let sequence = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  async function fetchOnce(mine: number) {
    const data = await getFundPortfolioBriefing(counterpartyId);
    if (mine !== sequence) return;
    briefing = data.briefing;
    generating = data.generating;
    if (data.error && !data.generating) error = data.error;
    clearTimeout(timer);
    if (generating) timer = setTimeout(() => void fetchOnce(mine).catch(() => {}), POLL_MS);
  }

  $effect(() => {
    const id = counterpartyId;
    untrack(() => {
      const mine = ++sequence;
      clearTimeout(timer);
      briefing = null;
      generating = false;
      error = '';
      if (!id) return;
      loading = true;
      fetchOnce(mine)
        .catch((err) => { if (mine === sequence) error = (err as Error)?.message || 'Could not load the briefing.'; })
        .finally(() => { if (mine === sequence) loading = false; });
    });
    return () => { sequence++; clearTimeout(timer); };
  });

  async function generate() {
    if (generating || readOnly) return;
    const mine = sequence;
    generating = true;
    error = '';
    try {
      await generateFundPortfolioBriefing(counterpartyId);
      if (mine === sequence) await fetchOnce(mine);
    } catch (err) {
      if (mine !== sequence) return;
      generating = false;
      error = (err as Error)?.message || 'Could not start the briefing.';
    }
  }

  // Footnote numbers follow first citation, so the list below reads in order.
  const cited = $derived.by(() => {
    if (!briefing) return [] as Source[];
    const byId = new Map(briefing.sources.map(source => [source.id, source]));
    const order: Source[] = [];
    for (const item of [...briefing.status.points, ...briefing.facts])
      for (const id of item.source_ids) {
        const source = byId.get(id);
        if (source && !order.includes(source)) order.push(source);
      }
    return order;
  });
  const numberOf = (id: string) => cited.findIndex(source => source.id === id) + 1;
  const factGroups = $derived.by(() => {
    const groups = new Map<string, PortfolioBriefing['facts']>();
    for (const fact of briefing?.facts || []) groups.set(fact.category, [...(groups.get(fact.category) || []), fact]);
    return [...groups];
  });
  const STATUS_CLASS: Record<string, string> = {
    'On track': 'ok', 'Needs attention': 'watch', 'At risk': 'risk', Unclear: 'unclear',
  };
  const updated = $derived(briefing ? new Date(briefing.generated_at).toLocaleDateString(undefined,
    { month: 'short', day: 'numeric', year: 'numeric' }) : '');
  const basisLine = $derived(briefing ? [
    `${briefing.basis.messages} email${briefing.basis.messages === 1 ? '' : 's'}`,
    ...(briefing.basis.notes ? [`${briefing.basis.notes} team note${briefing.basis.notes === 1 ? '' : 's'}`] : []),
  ].join(' · ') : '');
  const opensFromHere = (source: Source) => !!onOpenSource && source.kind === 'email' && !!source.message_id;
</script>

{#snippet refs(ids: string[])}
  {#each ids as id, i (id)}
    {@const source = cited.find(item => item.id === id)}
    {#if source}
      {#if opensFromHere(source)}
        <button type="button" class="pbr-fn" class:cont={i > 0} title={source.label}
                onclick={() => onOpenSource?.(source)}>{numberOf(id)}</button>
      {:else}
        <sup class="pbr-fn" class:cont={i > 0} title={source.label}>{numberOf(id)}</sup>
      {/if}
    {/if}
  {/each}
{/snippet}

<div class="brf pbr">
  {#if error}<p class="brf-alert" role="alert">{error}</p>{/if}

  {#if !briefing}
    <div class="empty-state brf-empty">
      {#if loading}
        <p class="brf-empty-h" role="status">Loading the briefing…</p>
      {:else if generating}
        <p class="brf-empty-h" role="status">{agent} is writing the briefing…</p>
        <p class="brf-empty-p">This takes about a minute.</p>
      {:else}
        <p class="brf-empty-h">No briefing yet</p>
        <p class="brf-empty-p">
          {agent} writes a status update and the key facts about {companyName} from the
          synced email threads and team notes your team has with them. Every line carries its source.
        </p>
        {#if !readOnly}
          <button type="button" class="btn-primary" onclick={generate}>Generate briefing</button>
        {/if}
      {/if}
    </div>
  {:else}
    <div class="brf-tools">
      <span class="brf-who">
        <span class="spark" aria-hidden="true">✦</span>
        Prepared by {agent} · updated {updated}{#if basisLine}{' · '}{basisLine}{/if}
      </span>
      {#if !readOnly}
        <span class="brf-tools-r">
          <button type="button" class="today-mini-btn" disabled={generating} onclick={generate}>
            {generating ? 'Regenerating…' : 'Regenerate'}
          </button>
        </span>
      {/if}
    </div>

    <article class="pbr-paper">
      <p class="pbr-status {STATUS_CLASS[briefing.status.label] || 'unclear'}">{briefing.status.label}</p>
      <h2 class="brf-lede">{briefing.status.headline}</h2>

      <section class="pbr-sec" aria-labelledby="pbr-update">
        <h3 id="pbr-update">Status update</h3>
        <ul class="pbr-list">
          {#each briefing.status.points as point, i (i)}
            <li>{point.text}{@render refs(point.source_ids)}</li>
          {/each}
        </ul>
      </section>

      {#if factGroups.length}
        <section class="pbr-sec" aria-labelledby="pbr-facts">
          <h3 id="pbr-facts">Key facts</h3>
          <dl class="pbr-facts">
            {#each factGroups as [category, facts] (category)}
              <dt>{category}</dt>
              <dd>
                <ul class="pbr-list">
                  {#each facts as fact, i (i)}
                    <li>{fact.text}{@render refs(fact.source_ids)}</li>
                  {/each}
                </ul>
              </dd>
            {/each}
          </dl>
        </section>
      {/if}

      <section class="pbr-sec" aria-labelledby="pbr-record">
        <h3 id="pbr-record">On record</h3>
        <dl class="pbr-record">
          {#each briefing.record as item (item.label)}
            <dt>{item.label}</dt><dd>{item.value}</dd>
          {/each}
        </dl>
      </section>

      {#if cited.length}
        <details class="brf-srcs pbr-srcs">
          <summary>Sources <span class="n">({cited.length})</span></summary>
          <ol class="pbr-sources">
            {#each cited as source (source.id)}
              <li>
                {#if opensFromHere(source)}
                  <button type="button" class="brf-link" onclick={() => onOpenSource?.(source)}>{source.label}</button>
                {:else}{source.label}{/if}
                <span class="k">{source.kind === 'record' ? 'workspace record' : source.kind}</span>
              </li>
            {/each}
          </ol>
        </details>
      {/if}
    </article>
  {/if}
</div>

<style>
  .pbr-paper { max-width: 760px; padding: 6px 0 56px; font-size: 14px; line-height: 1.6; }
  .pbr-status {
    display: inline-block; margin: 8px 0 0; padding: 1px 8px; border-radius: 999px;
    font-size: 11.5px; font-weight: 600; border: 1px solid currentColor;
  }
  .pbr-status.ok { color: var(--green-ink); }
  .pbr-status.watch { color: var(--orange-ink); }
  .pbr-status.risk { color: var(--red-ink); }
  .pbr-status.unclear { color: var(--text-dim); }
  .pbr-sec { margin-top: 26px; border-top: 1px solid var(--border); padding-top: 12px; }
  .pbr-sec h3 { margin: 0 0 8px; font-size: 12.5px; font-weight: 600; color: var(--text-muted); }
  .pbr-list { margin: 0; padding-left: 18px; }
  .pbr-list li + li { margin-top: 4px; }
  .pbr-facts { display: grid; grid-template-columns: 120px minmax(0, 1fr); gap: 8px 16px; margin: 0; }
  .pbr-facts dt, .pbr-record dt { font-size: 12px; color: var(--text-dim); padding-top: 2px; }
  .pbr-facts dd, .pbr-record dd { margin: 0; min-width: 0; }
  .pbr-record { display: grid; grid-template-columns: 160px minmax(0, 1fr); gap: 4px 16px; margin: 0; font-size: 13px; }
  .pbr-fn {
    margin-left: 1px; padding: 0; border: 0; background: none;
    color: var(--blue); font-size: 10px; font-weight: 600; line-height: 0; vertical-align: super;
  }
  button.pbr-fn { cursor: pointer; }
  button.pbr-fn:hover { text-decoration: underline; }
  .pbr-fn.cont::before { content: ','; color: var(--text-dim); }
  /* The LP list sits under a 60px section-number gutter; this page has none. */
  .pbr-srcs { margin: 26px 0 0; }
  .pbr-sources { margin: 8px 0 0; padding-left: 20px; font-size: 12.5px; }
  .pbr-sources .k { margin-left: 6px; color: var(--text-dim); font-size: 11px; }
  @media (max-width: 640px) {
    .pbr-facts, .pbr-record { grid-template-columns: minmax(0, 1fr); }
  }
</style>
